import React, { useRef, useState, useEffect } from "react";
import prodigy from "../../Assets/brand/ProdiogyIcon.svg";
import backArrow from "../../Assets/icons/backArrow.svg";
import QuestionMarkCircle from "../../Assets/icons/QuestionMarkCircle.svg";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { firstFactorAuthApi, resendOtpApi } from "../../Apis/login";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { clearLoginMeta } from "../../redux/slices/LoginBetaSlice";
import { handleFirstFactorAuthSuccess } from "../../auth/handleLoginSuccess";
import { getOrCreateDeviceId } from "../../utils/deviceId";
const OTP_LENGTH = 6;

const TwoFactorAuthVerfication: React.FC = () => {
  const { loginToken } = useAppSelector((state) => state.loginMeta);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(30);
  const [error, setError] = useState<string>("");
  const inputsRef = useRef<HTMLInputElement[]>([]);

  // Timer countdown effect
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);
    setError(""); // Clear error when user types

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const updatedOtp = [...otp];
    for (let i = 0; i < pasted.length && index + i < OTP_LENGTH; i += 1) {
      updatedOtp[index + i] = pasted[i];
    }
    setOtp(updatedOtp);
    setError("");
    const nextIndex = Math.min(index + pasted.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  const handleResend = async () => {
    if (timer === 0 && loginToken) {
      try {
        await resendOtpApi(loginToken);
        setTimer(30);
        setOtp(Array(OTP_LENGTH).fill(""));
        setError("");
        inputsRef.current[0]?.focus();
        toast.success("OTP resent successfully");
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || "Failed to resend OTP");
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (!otp.some((d) => d === "")) {
      setError(""); // Clear error before submitting
      try {
        const response = await firstFactorAuthApi(
          loginToken,
          otp.join(""),
          getOrCreateDeviceId(),
          true
        );
        await handleFirstFactorAuthSuccess(dispatch, navigate, response);
      } catch (error) {
        if (error instanceof AxiosError) {
          if (
            error.response?.data?.message === "jwt expired" ||
            error.response?.data?.message === "jwt malformed" ||
            error.response?.data?.error === "loginToken and code are required"
          ) {
            toast.error("Session expired. Please log in again");
            dispatch(clearLoginMeta());
            navigate("/");
          } else if (error.response?.data?.message === "Invalid code") {
            setError("Incorrect OTP");
          } else {
            toast.error("An unexpected error occurred. Please try again.");
          }
        }
      }
    }
  };

  return (
    <div
      id="two-factor-auth-container"
      className="w-full max-w-md h-full flex flex-col"
    >
      {/* Header */}
      <div
        id="two-factor-auth-header"
        className="flex items-center justify-between mb-10 mt-10"
      >
        <button
          id="two-factor-auth-back-button"
          type="button"
          className="text-[14px] font-medium text-[#080F1A] font-outfit leading-[150%] flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
          onClick={() => navigate(-1)}
          aria-label="Go back to previous page"
        >
          <img
            id="two-factor-auth-back-icon"
            src={backArrow}
            alt=""
            aria-hidden="true"
          />
          Go Back
        </button>
        <button
          id="two-factor-auth-help-button"
          type="button"
          className="focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
          aria-label="Help"
        >
          <img
            id="two-factor-auth-help-icon"
            src={QuestionMarkCircle}
            alt="Help"
          />
        </button>
      </div>

      <div
        id="two-factor-auth-content"
        className="flex-1 flex flex-col justify-center"
      >
        {/* Logo */}
        <img
          id="two-factor-auth-logo"
          src={prodigy}
          alt="Prodigy Health"
          className="w-40 mb-6"
        />

        {/* Title */}
        <span
          id="two-factor-auth-heading"
          className="text-[18px] font-medium text-[var(--Text-Black,#080F1A)] font-outfit leading-[120%] mb-2"
        >
          Two-Factor Authentication
        </span>
        <p
          id="two-factor-auth-description"
          className="text-[14px] font-normal text-[var(--Text-Light-Grey,#647491)] font-outfit leading-[150%] mb-8"
        >
          We've sent a 6-digit OTP to your registered mobile number and email.
        </p>

        {/* OTP Inputs */}
        <div
          id="two-factor-auth-otp-container"
          className="flex gap-3 mb-4"
          role="group"
          aria-label="Enter the 6-digit one-time passcode"
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`two-factor-auth-otp-input-${index + 1}`}
              ref={(el) => {
                if (el) inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              aria-invalid={Boolean(error)}
              aria-describedby={
                error ? "two-factor-auth-error-message" : undefined
              }
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              className="w-12 h-12 text-center text-lg rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <p
            id="two-factor-auth-error-message"
            role="alert"
            className="text-[14px] font-normal text-red-500 font-outfit leading-[150%] mb-2"
          >
            {error}
          </p>
        )}

        {/* Resend */}
        <p
          id="two-factor-auth-resend-container"
          className="text-[14px] font-normal text-[#647491] font-outfit leading-[150%] mb-4"
        >
          Didn't get OTP?{" "}
          <button
            id="two-factor-auth-resend-button"
            type="button"
            onClick={handleResend}
            disabled={timer > 0}
            className={`text-[14px] font-medium font-outfit leading-[150%] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded ${
              timer > 0
                ? "text-gray-400 cursor-not-allowed no-underline"
                : "text-[#0566FF]"
            }`}
            aria-label={
              timer > 0 ? `Resend OTP in ${timer} seconds` : "Resend OTP"
            }
          >
            Resend ({timer < 10 ? `00:0${timer}` : `00:${timer}`})
          </button>
        </p>

        {/* Trust Device */}
        <div
          id="two-factor-auth-trust-device-container"
          className="flex items-start gap-2 mb-6"
        >
          <input
            type="checkbox"
            id="two-factor-auth-trust-device-checkbox"
            className="mt-1 focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
            aria-label="Trust this device"
          />
          <label
            htmlFor="two-factor-auth-trust-device-checkbox"
            id="two-factor-auth-trust-device-label"
            className="text-[12px] font-normal text-[#647491] font-outfit leading-[150%] cursor-pointer"
          >
            Trust this device (No password required here for 90 days)
          </label>
        </div>

        {/* Submit */}
        <button
          id="two-factor-auth-submit-button"
          type="button"
          onClick={() => handleSubmit()}
          disabled={otp.some((d) => d === "")}
          className="w-full bg-[#A9C3FF] hover:bg-[#0566FF] text-white text-center text-[14px] font-medium font-outfit leading-[150%] py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          aria-label="Submit OTP"
        >
          Submit
        </button>
      </div>

      <p
        id="two-factor-auth-footer"
        className="mt-auto mb-10 text-xs text-[#647491]"
      >
        © 2025 Arete Healthtech Pvt. Ltd. All rights reserved.
      </p>
    </div>
  );
};

export default TwoFactorAuthVerfication;
